import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";

// Public
import Landing from "@/pages/public/Landing";
import Pricing from "@/pages/public/Pricing";
import Features from "@/pages/public/Features";
import GSTBilling from "@/pages/public/GSTBilling";
import FAQ from "@/pages/public/FAQ";
import Contact from "@/pages/public/Contact";
import { Privacy, Terms } from "@/pages/public/Legal";

// Auth
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";

// App
import Dashboard from "@/pages/Dashboard";
import Parties from "@/pages/Parties";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Items from "@/pages/Items";
import Sales from "@/pages/Sales";
import Purchases from "@/pages/Purchases";
import CashBank from "@/pages/CashBank";
import Products from "@/pages/Products";
import Services from "@/pages/Services";
import Inventory from "@/pages/Inventory";
import Invoices from "@/pages/Invoices";
import Quotations from "@/pages/Quotations";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import GST from "@/pages/GST";
import Reports from "@/pages/Reports";
import CRM from "@/pages/CRM";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Employees from "@/pages/Employees";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminPayments from "@/pages/admin/AdminPayments";

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function AdminGuard() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["super_admin", "admin"].includes(user.role)) return <Navigate to="/app" replace />;
  return <AdminLayout />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/gst-billing" element={<GSTBilling />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

            {/* App */}
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/parties" element={<Protected><Parties /></Protected>} />
            <Route path="/app/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/app/suppliers" element={<Protected><Suppliers /></Protected>} />
            <Route path="/app/items" element={<Protected><Items /></Protected>} />
            <Route path="/app/products" element={<Protected><Products /></Protected>} />
            <Route path="/app/services" element={<Protected><Services /></Protected>} />
            <Route path="/app/sales" element={<Protected><Sales /></Protected>} />
            <Route path="/app/purchases" element={<Protected><Purchases /></Protected>} />
            <Route path="/app/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/app/quotations" element={<Protected><Quotations /></Protected>} />
            <Route path="/app/payments" element={<Protected><Payments /></Protected>} />
            <Route path="/app/cash-bank" element={<Protected><CashBank /></Protected>} />
            <Route path="/app/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/app/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/app/gst" element={<Protected><GST /></Protected>} />
            <Route path="/app/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/app/crm" element={<Protected><CRM /></Protected>} />
            <Route path="/app/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/app/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/app/employees" element={<Protected><Employees /></Protected>} />
            <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/app/subscription" element={<Protected><Subscription /></Protected>} />

            {/* Super Admin */}
            <Route path="/admin" element={<AdminGuard />}>
              <Route index element={<AdminDashboard />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="payments" element={<AdminPayments />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
